import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
	X, ArrowRight
} from 'lucide-react';
import api, { getClient, generateTrackingToken, formatIQD } from '../utils/api';
import toast from 'react-hot-toast';
import {
	S, Dual, td, pill, btn
} from '../components/SharedUI';

export default function ClientProfilePage() {
	const { id: clientId } = useParams();
	const navigate = useNavigate();
	const [data, setData] = useState(null);
	const [tab, setTab] = useState('invoices');
	const [generatingLink, setGeneratingLink] = useState(false);

	useEffect(() => {
		getClient(clientId).then(setData).catch(() => {
			toast.error('حدث خطأ أثناء تحميل بيانات العميل');
			navigate('/clients');
		});
	}, [clientId, navigate]);

	const handleGenerateLink = async () => {
		setGeneratingLink(true);
		try {
			const res = await generateTrackingToken(clientId);
			setData({ ...data, tracking_token: res.tracking_token });
			toast.success('تم إنشاء رابط التتبع بنجاح');
		} catch (err) {
			toast.error('حدث خطأ أثناء إنشاء الرابط');
		} finally {
			setGeneratingLink(false);
		}
	};

	const getTrackingLink = () => {
		return `${window.location.origin}/track/${data.tracking_token}`;
	};

	const copyTrackingLink = () => {
		if (!data.tracking_token) return;
		navigator.clipboard.writeText(getTrackingLink());
		toast.success('تم نسخ رابط التتبع');
	};

	const sendTrackingLinkWhatsApp = () => {
		if (!data.tracking_token || !data.phone) return;
		const textMsg = encodeURIComponent(`مرحباً ${data.name}،\nيمكنك الآن متابعة حالة خدماتك وفواتيرك والاطلاع على التقرير الخاص بك من خلال الرابط المخصص التالي:\n\n${getTrackingLink()}\n\nهذا الرابط خاص بك، يرجى عدم مشاركته.`);
		window.open(`https://wa.me/${data.phone.replace(/\D/g, '')}?text=${textMsg}`, '_blank');
	};

	if (!data) return (
		<div className="flex items-center justify-center h-64 animate-fade-in">
			<div className="flex flex-col items-center gap-3 text-gray-400">
				<div className="w-8 h-8 border-4 border-gray-200 dark:border-white/10 border-t-primary rounded-full animate-spin"></div>
				<p className="font-bold">جاري تحميل ملف العميل...</p>
			</div>
		</div>
	);

	const { projects = [], subscriptions = [], invoices = [], deliverables = [], stats = {} } = data;
	const activeSubsList = subscriptions.filter(s => s.status === 'active');
	const activeSubs = activeSubsList.length;
	const mainSub = activeSubs > 0 ? activeSubsList[0] : null;

	const VT_LABEL = {
		reels: 'ريلز', ugc: 'UGC', brand: 'براند', story: 'ستوري',
		ai_influencer: 'AI Influencer', other: 'أخرى'
	};

	const INV_STATUS = {
		unpaid: { label: 'غير مدفوع', color: '#E74C3C', bg: '#FEE2E220' },
		partial: { label: 'جزئي', color: '#E67E22', bg: '#FEF3C720' },
		paid: { label: 'مدفوع', color: '#17A589', bg: '#D1FAE520' },
		overdue: { label: 'متأخر', color: '#C0392B', bg: '#FDEDEC' },
		cancelled: { label: 'ملغي', color: '#95A5A6', bg: '#F8F9FA' },
	};

	return (
		<div className="max-w-7xl mx-auto space-y-6 animate-fade-in" style={{ paddingBottom: '40px' }}>
			{/* Header Buttons */}
			<div className="flex items-center mb-4">
				<button
					onClick={() => navigate(-1)}
					className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors font-bold text-sm bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 px-4 py-2 rounded-xl shadow-sm hover:border-primary/30"
				>
					<ArrowRight size={18} />
					رجوع
				</button>
			</div>

			<div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, white)', borderRadius: 20, width: '100%', fontFamily: 'Tajawal, sans-serif', direction: 'rtl', boxShadow: '0 4px 20px rgba(0,0,0,.05)', border: '1px solid var(--border-light, #E2E8F0)' }}>

				{/* Profile Header */}
				<div style={{ background: 'linear-gradient(135deg, #0F1E33, #1A3A5C)', borderRadius: '20px 20px 0 0', padding: '30px 40px', color: 'white' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
						<div>
							<div style={{ fontSize: 26, fontWeight: 900, marginBottom: 8, letterSpacing: '-0.5px' }}>{data.name}</div>
							<div className="dark:!text-gray-400" style={{ display: 'flex', gap: 16, fontSize: 14, color: 'var(--text-muted, #94A3B8)', flexWrap: 'wrap', alignItems: 'center' }}>
								{data.phone && (
									<a href={'https://wa.me/' + data.phone.replace(/\D/g, '')} target="_blank" rel="noreferrer"
										style={{ color: '#4ADE80', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'bold' }}>
										📱 {data.phone}
									</a>
								)}
								{data.email && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>✉️ {data.email}</span>}
								<span style={{
									padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
									background: data.status === 'active' ? '#16A34A' : '#991B1B', color: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
								}}>
									{data.status === 'active' ? 'عميل نشط' : 'غير نشط'}
								</span>
								<span style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '20px', fontSize: 12 }}>
									🏢 {data.type === 'company' ? 'شركة / مؤسسة' : data.type === 'individual' ? 'فرد / صانع محتوى' : data.type}
								</span>
							</div>
						</div>

						<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
							{data.tracking_token ? (
								<>
									<button onClick={copyTrackingLink} title="نسخ رابط التتبع" style={{ background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.2)', cursor: 'pointer', color: 'white', borderRadius: 12, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 'bold', transition: 'all 0.2s' }}>
										🔗 نسخ رابط التتبع
									</button>
									{data.phone && (
										<button onClick={sendTrackingLinkWhatsApp} title="إرسال الرابط عبر واتساب" style={{ background: '#25D366', border: 'none', cursor: 'pointer', color: 'white', borderRadius: 12, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 'bold', boxShadow: '0 4px 10px rgba(37, 211, 102, 0.3)', transition: 'all 0.2s' }}>
											📱 واتساب
										</button>
									)}
								</>
							) : (
								<button onClick={handleGenerateLink} disabled={generatingLink} style={{ background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.2)', cursor: 'pointer', color: 'white', borderRadius: 12, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 'bold', transition: 'all 0.2s' }}>
									{generatingLink ? 'جاري الإنشاء...' : '✨ إنشاء رابط للعميل'}
								</button>
							)}
						</div>
					</div>
				</div>

				{/* KPI boxes */}
				<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, padding: '24px 32px 0' }}>
					{[
						{ label: 'إجمالي الفواتير', value: formatIQD(stats.lifetime_value_iqd), color: 'var(--text-dark-h, #0F1E33)' },
						{ label: 'المحصّل', value: formatIQD(stats.total_paid_iqd), color: '#17A589' },
						{ label: 'الرصيد المعلق (الديون)', value: formatIQD(stats.outstanding_iqd), color: (stats.outstanding_iqd || 0) > 0 ? '#E74C3C' : '#17A589' },
						{ label: 'اشتراكات نشطة', value: activeSubs, color: '#9B8EC4' },
					].map(k => (
						<div key={k.label} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 14, padding: '16px 20px', border: '1px solid var(--border-light, #E2E8F0)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
							<div className="dark:!text-gray-400" style={{ fontSize: 13, color: 'var(--text-gray, #64748B)', marginBottom: 8, fontWeight: 700 }}>{k.label}</div>
							<div style={{ fontSize: 18, fontWeight: 900, color: k.color }}>{k.value}</div>
						</div>
					))}
				</div>

				{/* Active Subscription Summary */}
				{mainSub && (
					<div style={{ padding: '24px 32px 0' }}>
						<div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 16, padding: '24px', border: '1px solid var(--border-light, #E2E8F0)', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' }}>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
								<div>
									<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
										<div className="dark:!text-gray-100" style={{ fontSize: 18, fontWeight: 900, color: 'var(--text-dark-h, #0F1E33)' }}>الاشتراك الحالي: {mainSub.package_name}</div>
										<span style={{ ...pill('#D1FAE5', '#065F46'), fontSize: 12, padding: '4px 10px', fontWeight: 800 }}>نشط الآن</span>
									</div>
									<div className="dark:!text-gray-400" style={{ fontSize: 14, color: 'var(--text-gray, #64748B)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
										<span><strong style={{ color: 'var(--text-gray, #475569)' }}>بدأ في:</strong> {mainSub.period_start}</span>
										<span style={{ color: '#CBD5E1' }}>|</span>
										<span><strong style={{ color: 'var(--text-gray, #475569)' }}>ينتهي في:</strong> {mainSub.period_end}</span>
									</div>
								</div>
							</div>

							<div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, white)', padding: '20px', borderRadius: 12, border: '1px solid var(--border-light, #E2E8F0)' }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 12 }}>
									<span className="dark:!text-gray-400" style={{ color: 'var(--text-gray, #64748B)', fontWeight: 700 }}>تم تسليم: <strong style={{ color: '#17A589', fontSize: 16 }}>{mainSub.used_seconds}</strong> ثانية</span>
									<span className="dark:!text-gray-100" style={{ color: 'var(--text-dark-h, #0F1E33)', fontWeight: 800 }}>
										المتبقي: <strong style={{ fontSize: 18, color: '#4F46E5' }}>{mainSub.remaining_seconds}</strong> ثانية
										<span className="dark:!text-gray-400" style={{ color: 'var(--text-muted, #94A3B8)', fontWeight: 600, fontSize: 13 }}> (من أصل {mainSub.total_seconds})</span>
									</span>
								</div>
								<div style={{ height: 12, background: 'var(--bg-flat-100, #F1F5F9)', borderRadius: 8, overflow: 'hidden' }}>
									<div style={{
										height: '100%', borderRadius: 8,
										width: `${Math.min((mainSub.used_seconds / Math.max(mainSub.total_seconds, 1)) * 100, 100)}%`,
										background: (mainSub.used_seconds / Math.max(mainSub.total_seconds, 1)) > 0.8 ? '#E74C3C' : '#10B981',
										transition: 'width 1s ease-in-out'
									}} />
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Tabs */}
				<div style={{ display: 'flex', gap: 8, padding: '24px 32px 0', borderBottom: '2px solid var(--border-light, #E2E8F0)', overflowX: 'auto', marginTop: 10 }}>
					{[
						['invoices', 'الفواتير (' + invoices.length + ')'],
						['subscriptions', 'الاشتراكات (' + subscriptions.length + ')'],
						['deliverables', 'التسليمات (' + deliverables.length + ')'],
						['projects', 'المشاريع (' + projects.length + ')']
					].map(([v, l]) => (
						<button key={v} onClick={() => setTab(v)} style={{
							padding: '12px 24px', border: 'none', cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
							fontSize: 14, fontWeight: 700, borderRadius: '12px 12px 0 0', transition: 'all .2s',
							background: tab === v ? 'var(--bg-flat-white, white)' : 'transparent',
							color: tab === v ? '#9B8EC4' : 'var(--text-gray, #64748B)',
							borderBottom: tab === v ? '3px solid #9B8EC4' : '3px solid transparent',
							marginBottom: -2, whiteSpace: 'nowrap'
						}}>{l}</button>
					))}
				</div>

				{/* Tab content */}
				<div style={{ padding: '0 32px 32px' }}>
					{tab === 'invoices' && (
						<div style={{ marginTop: 20 }}>
							{invoices.length === 0 ? (
								<div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ color: 'var(--text-muted, #94A3B8)', textAlign: 'center', padding: '60px 20px', background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 16, border: '1px dashed #CBD5E1' }}>
									<p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد فواتير مرتبطة بهذا العميل</p>
								</div>
							) : (
								<div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ overflowX: 'auto', background: 'var(--bg-flat-white, white)', borderRadius: 16, border: '1px solid var(--border-light, #E2E8F0)' }}>
									<table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right' }}>
										<thead>
											<tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
												{['رقم الفاتورة', 'تاريخ الإصدار', 'المبلغ الإجمالي', 'المدفوع', 'حالة الدفع', 'إجراء'].map((h, idx) => (
													<th key={idx} className="dark:!text-gray-400" style={{ padding: '14px 20px', fontSize: 13, color: 'var(--text-gray, #64748B)', fontWeight: 800, borderBottom: '2px solid var(--border-light, #E2E8F0)' }}>{h}</th>
												))}
											</tr>
										</thead>
										<tbody>
											{invoices.map((inv, i) => {
												const st = INV_STATUS[inv.payment_status] || INV_STATUS.unpaid;
												const remain = (inv.total_iqd || 0) - (inv.paid_iqd || 0);
												return (
													<tr key={inv.id} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: 'var(--bg-flat-white, white)' }} className="hover:bg-gray-50 dark:hover:bg-white/5 dark:bg-white/5/50 transition-colors">
														<td style={{ padding: '14px 20px', fontWeight: 800, color: '#9B8EC4', fontSize: 14 }}>{inv.invoice_number}</td>
														<td className="dark:!text-gray-400" style={{ padding: '14px 20px', color: 'var(--text-gray, #64748B)', fontSize: 13, fontWeight: 600 }}>{inv.issue_date}</td>
														<td style={{ padding: '14px 20px' }}><Dual iqd={inv.total_iqd} usd={inv.total_usd} bold /></td>
														<td style={{ padding: '14px 20px' }}><Dual iqd={inv.paid_iqd} usd={inv.paid_usd} /></td>
														<td style={{ padding: '14px 20px' }}><span style={pill(st.bg, st.color)}>{st.label}</span></td>
														<td style={{ padding: '14px 20px' }}>
															{data.phone && (() => {
																const trackAppend = data.tracking_token ? `\n\n🔗 لمتابعة التفاصيل والفواتير عبر الرابط الخاص بك:\n${window.location.origin}/track/${data.tracking_token}` : '';
																const textMsg = encodeURIComponent(`مرحباً ${data.name}،\nإليك تفاصيل الفاتورة الخاصة بك:\n\n📄 رقم الفاتورة: ${inv.invoice_number}\n💰 الإجمالي: ${formatIQD(inv.total_iqd)}\n✅ المدفوع: ${formatIQD(inv.paid_iqd)}\n⏳ المتبقي: ${formatIQD(remain)}\n\nتاريخ الاستحقاق: ${inv.due_date || '—'}${trackAppend}\n\nشكراً لتعاملك معنا — MS MEDIA`);
																return (
																	<a href={'https://wa.me/' + data.phone.replace(/\D/g, '') + '?text=' + textMsg} target="_blank" rel="noreferrer"
																		style={{ color: '#065F46', background: '#D1FAE5', padding: '6px 12px', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.2s' }}
																		title="إرسال الفاتورة عبر واتساب">
																		📱 واتساب
																	</a>
																);
															})()}
														</td>
													</tr>
												);
											})}
										</tbody>
										<tfoot>
											<tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
												<td colSpan={2} style={{ padding: '16px 20px', fontWeight: 800, fontSize: 14 }}>إجمالي فواتير العميل</td>
												<td style={{ padding: '16px 20px' }}><Dual iqd={invoices.reduce((s, i) => s + (i.total_iqd || 0), 0)} bold /></td>
												<td style={{ padding: '16px 20px' }}><Dual iqd={invoices.reduce((s, i) => s + (i.paid_iqd || 0), 0)} /></td>
												<td colSpan={2} />
											</tr>
										</tfoot>
									</table>
								</div>
							)}
						</div>
					)}

					{tab === 'subscriptions' && (
						<div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
							{subscriptions.length === 0 ? (
								<div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ gridColumn: '1 / -1', color: 'var(--text-muted, #94A3B8)', textAlign: 'center', padding: '60px 20px', background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 16, border: '1px dashed #CBD5E1' }}>
									<p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد اشتراكات مسجلة</p>
								</div>
							) : subscriptions.map(s => (
								<div key={s.id} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 16, padding: '20px', border: '1px solid var(--border-light, #E2E8F0)', display: 'flex', flexDirection: 'column', gap: 12 }}>
									<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
										<div>
											<div className="dark:!text-gray-100" style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)' }}>{s.package_name}</div>
											<div className="dark:!text-gray-400" style={{ fontSize: 13, color: 'var(--text-gray, #64748B)', marginTop: 4, fontWeight: 600 }}>{s.period_start} — {s.period_end}</div>
										</div>
										<span style={{
											...pill(
												s.status === 'active' ? '#D1FAE5' : s.status === 'suspended' ? '#FEF3C7' : '#FEE2E2',
												s.status === 'active' ? '#065F46' : s.status === 'suspended' ? '#92400E' : '#991B1B'
											), fontSize: 12, padding: '4px 10px'
										}}>
											{s.status === 'active' ? 'اشتراك نشط' : s.status === 'suspended' ? 'موقوف' : 'منتهي'}
										</span>
									</div>

									<div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, white)', padding: '12px', borderRadius: 10, border: '1px solid var(--border-light, #F1F5F9)', marginTop: 4 }}>
										<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
											<span className="dark:!text-gray-400" style={{ color: 'var(--text-gray, #64748B)', fontWeight: 600 }}>مستخدَم: {s.used_seconds}ث</span>
											<span className="dark:!text-gray-100" style={{ color: 'var(--text-dark-h, #0F1E33)', fontWeight: 800 }}>متبقي: {s.remaining_seconds}ث <span className="dark:!text-gray-400" style={{ color: 'var(--text-muted, #94A3B8)', fontWeight: 500 }}>(من {s.total_seconds}ث)</span></span>
										</div>
										<div style={{ height: 8, background: 'var(--bg-flat-100, #F1F5F9)', borderRadius: 6, overflow: 'hidden' }}>
											<div style={{
												height: '100%', borderRadius: 6,
												width: `${Math.min((s.used_seconds / Math.max(s.total_seconds, 1)) * 100, 100)}%`,
												background: (s.used_seconds / Math.max(s.total_seconds, 1)) > 0.8 ? '#E74C3C' : '#17A589'
											}} />
										</div>
									</div>
								</div>
							))}
						</div>
					)}

					{tab === 'deliverables' && (
						<div style={{ marginTop: 20 }}>
							{deliverables.length === 0 ? (
								<div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ color: 'var(--text-muted, #94A3B8)', textAlign: 'center', padding: '60px 20px', background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 16, border: '1px dashed #CBD5E1' }}>
									<p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد تسليمات مسجلة لهذا العميل</p>
								</div>
							) : (
								<div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ overflowX: 'auto', background: 'var(--bg-flat-white, white)', borderRadius: 16, border: '1px solid var(--border-light, #E2E8F0)' }}>
									<table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right' }}>
										<thead>
											<tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
												{['عنوان الفيديو', 'النوع', 'الثواني المنفذة', 'تاريخ التسليم', 'الباقة / المشروع المرتبط', 'حالة الدفع'].map((h, idx) => (
													<th key={idx} className="dark:!text-gray-400" style={{ padding: '14px 20px', fontSize: 13, color: 'var(--text-gray, #64748B)', fontWeight: 800, borderBottom: '2px solid var(--border-light, #E2E8F0)' }}>{h}</th>
												))}
											</tr>
										</thead>
										<tbody>
											{deliverables.map((d, i) => (
												<tr key={d.id} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: 'var(--bg-flat-white, white)' }} className="hover:bg-gray-50 dark:hover:bg-white/5 dark:bg-white/5/50 transition-colors">
													<td style={{ padding: '14px 20px', fontWeight: 700, fontSize: 14 }}>{d.title}</td>
													<td style={{ padding: '14px 20px' }}>
														<span style={pill('#F0ECF7', '#9B8EC4')}>{VT_LABEL[d.video_type] || d.video_type || '—'}</span>
													</td>
													<td style={{ padding: '14px 20px', fontWeight: 900, color: '#17A589', fontSize: 15 }}>{d.seconds_used || d.total_seconds_used} ثانية</td>
													<td className="dark:!text-gray-400" style={{ padding: '14px 20px', color: 'var(--text-gray, #64748B)', fontSize: 13, fontWeight: 600 }}>{d.delivered_at || d.delivery_date}</td>
													<td className="dark:!text-gray-100" style={{ padding: '14px 20px', color: 'var(--text-dark-s, #374151)', fontSize: 13, fontWeight: 600 }}>{d.package_name || d.project_title || '—'}</td>
													<td style={{ padding: '14px 20px' }}>
														<span style={pill(
															d.sub_payment_status === 'paid' ? '#D1FAE5' : d.sub_payment_status === 'partial' ? '#FEF3C7' : '#FEE2E2',
															d.sub_payment_status === 'paid' ? '#065F46' : d.sub_payment_status === 'partial' ? '#92400E' : '#991B1B'
														)}>
															{d.sub_payment_status === 'paid' ? 'مدفوع الاشتراك' : d.sub_payment_status === 'partial' ? 'جزئي الدفع' : 'الاشتراك غير مدفوع'}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					)}

					{tab === 'projects' && (
						<div style={{ marginTop: 20 }}>
							{projects.length === 0 ? (
								<div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ color: 'var(--text-muted, #94A3B8)', textAlign: 'center', padding: '60px 20px', background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 16, border: '1px dashed #CBD5E1' }}>
									<p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد مشاريع مسجلة</p>
								</div>
							) : (
								<div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ overflowX: 'auto', background: 'var(--bg-flat-white, white)', borderRadius: 16, border: '1px solid var(--border-light, #E2E8F0)' }}>
									<table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right' }}>
										<thead>
											<tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
												{['اسم المشروع', 'المرحلة التشغيلية', 'حالة المشروع', 'قيمة المشروع'].map((h, idx) => (
													<th key={idx} className="dark:!text-gray-400" style={{ padding: '14px 20px', fontSize: 13, color: 'var(--text-gray, #64748B)', fontWeight: 800, borderBottom: '2px solid var(--border-light, #E2E8F0)' }}>{h}</th>
												))}
											</tr>
										</thead>
										<tbody>
											{projects.map((p, i) => {
												const STAGE_LABELS = { ideas: 'كتابة الأفكار', approving: 'موافقة الأفكار', executing: 'تنفيذ الأفكار', done: 'تم التنفيذ' };
												return (
													<tr key={p.id} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: 'var(--bg-flat-white, white)' }} className="hover:bg-gray-50 dark:hover:bg-white/5 dark:bg-white/5/50 transition-colors">
														<td className="dark:!text-gray-100" style={{ padding: '14px 20px', fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', fontSize: 14 }}>{p.title}</td>
														<td style={{ padding: '14px 20px', fontSize: 13, color: '#9B8EC4', fontWeight: 700 }}>
															{STAGE_LABELS[p.current_stage] || '—'}
														</td>
														<td style={{ padding: '14px 20px' }}>
															<span style={pill(
																p.status === 'done' || p.status === 'completed' ? '#D1FAE5' : p.status === 'in_progress' ? '#DBEAFE' : '#F1F5F9',
																p.status === 'done' || p.status === 'completed' ? '#065F46' : p.status === 'in_progress' ? '#1D4ED8' : 'var(--text-gray, #64748B)'
															)}>
																{p.status === 'done' || p.status === 'completed' ? 'تم التسليم' : p.status === 'in_progress' ? 'قيد العمل' : 'مسودة'}
															</span>
														</td>
														<td style={{ padding: '14px 20px', fontWeight: 800, fontSize: 14 }}>{formatIQD(p.total_iqd)}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>
					)}
				</div>

			</div >
		</div >
	);
}
